<template>
  <VaAccordion v-model="value">
    <VaCollapse v-if="item.route || item.children" :key="item.id">
      <template #header="{ value: isCollapsed }">
        <VaSidebarItem
          :to="item.children ? undefined : { name: item.route }"
          :active="routeHasActiveChild(item)"
          :active-color="activeColor"
          :text-color="textColor(item)"
          :aria-label="`${item.children ? 'Open category ' : 'Visit'} ${t(item.displayNameKey as string)}`"
          role="button"
          hover-opacity="0.10"
        >
          <VaSidebarItemContent class="py-1 pr-2" :class="`pl-${4 + level * 4}`">
            <VaIcon v-if="item.icon" aria-hidden="true" :name="item.icon" size="20px" :color="iconColor(item)" />
            <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
              {{ t(item.displayNameKey as string) }}
              <VaIcon v-if="item.children" :name="arrowDirection(isCollapsed)" size="20px" />
            </VaSidebarItemTitle>
          </VaSidebarItemContent>
        </VaSidebarItem>
      </template>
      <template #body>
        <div v-for="child in item.children" :key="child.id">
          <SidebarMenuItem
            v-if="child.children"
            :key="child.id"
            :item="child"
            :active-color="props.activeColor"
            :icon-color="props.iconColor"
            :text-color="props.textColor"
            :arrow-direction="props.arrowDirection"
            :handle-favorite="props.handleFavorite"
            :level="props.level + 1"
            :force-expand="props.forceExpand"
          />
          <VaSidebarItem
            v-else
            :to="{ name: child.route, params: child.routeParam ? JSON.parse(child.routeParam) : {} }"
            :active="isActiveChildRoute(child)"
            :active-color="activeColor"
            :text-color="textColor(child)"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-1 pr-2" :style="`padding-left: ${level + 2}rem`">
              <VaIcon v-if="child.icon" aria-hidden="true" :name="child.icon" size="20px" :color="iconColor(child)" />
              <VaSidebarItemTitle class="leading-5 font-semibold">
                {{ child.displayName ?? t(child.displayNameKey as string) }}
              </VaSidebarItemTitle>
              <VaIcon
                v-if="child.route"
                aria-hidden="true"
                name="favorite"
                size="20px"
                :class="{ 'material-symbols-outlined': !child.isFavorite }"
                :color="props.iconColor(child)"
                @click.prevent="props.handleFavorite(child)"
              />
            </VaSidebarItemContent>
          </VaSidebarItem>
        </div>
      </template>
    </VaCollapse>
  </VaAccordion>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue"
import SidebarMenuItem from "./SidebarMenuItem.vue"
import { MenuHierarchy } from "@/pages/admin/menuHierarchy/types"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"

const { t } = useI18n()
const value = ref<boolean[]>([])
const route = useRoute()

const props = defineProps<{
  item: MenuHierarchy
  activeColor: string
  iconColor: (item: MenuHierarchy) => string
  textColor: (item: MenuHierarchy) => string
  arrowDirection: (isCollapsed: boolean) => string
  handleFavorite: (item: MenuHierarchy) => void
  level: number
  forceExpand?: boolean
}>()

const isActiveChildRoute = (child: MenuHierarchy) => {
  if (route.name == "report-viewer") {
    const paramJson = child.routeParam ? JSON.parse(child.routeParam) : {}
    return route.params.id == paramJson.id
  }

  return route.name === child.route
}

const routeHasActiveChild = (section: MenuHierarchy) => {
  if (!section.children && route.name) {
    return isActiveRoute(section)
  } else if (section.children && route.name) {
    return section.children.some((item) => isActiveRoute(item))
  }

  return false
}

const isActiveRoute = (child: MenuHierarchy): boolean => {
  if (child.route === "report-viewer") {
    const paramJson = child.routeParam ? JSON.parse(child.routeParam) : {}
    return route.params.id == paramJson.id
  }
  return route!.name!.toString().endsWith(`${child.route}`)
}

const checkActive = () => {
  if (props.forceExpand) {
    value.value = [true]
    return
  }
  value.value = [routeHasActiveChild(props.item)]
}

watch(
  () => props.item,
  () => checkActive(),
  { immediate: true },
)

watch(
  () => props.forceExpand,
  () => checkActive(),
  { immediate: true },
)
</script>
